/**
 * Brief Modal — the dedicated, roomy Brief surface (Scoped Brief — brief-modal-spec).
 *
 * Opened from the outline (per section / question / single doc) AND the editor
 * header — same surface, same stores, no agent session. Holds the writer's
 * intent: what the plan says the unit must do, the writer's own notes, and three
 * readable lists — requirements, win themes, pinned documents — each with its AI
 * suggestions inline. One "Suggest with AI" action runs all three suggestion
 * sources. The Plan stays in the editor's AI panel; this modal only offers an
 * "Open editor" jump.
 *
 * Notes auto-save: debounced after typing stops, and flushed on blur, close,
 * unit change and unmount. Every brief write (notes, pins and a single
 * document's win themes share one optimistic-concurrency version) goes through
 * one serial queue, so a pin save never races an in-flight notes save into a
 * spurious 409. An outlined section's win themes are saved on the outline.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react'
import { Check, ChevronRight, FileText, Flag, Plus, X } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { AutoGrowTextarea } from '@/components/ui/auto-grow-textarea'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { GradientSparkleIcon } from '@/components/extraction/GradientSparkleIcon'
import { RequirementPickerPopover } from '@/components/requirement-coverage/RequirementPickerPopover'
import {
  useRequirementDirectory,
  type RequirementEntry,
} from '@/components/requirement-coverage/useRequirementDirectory'
import { EvidenceSelectDialog } from '@/components/drafting/EvidenceSelectDialog'
import {
  rememberDocumentNames,
  usePinnedDocumentNames,
} from '@/components/drafting/usePinnedDocumentNames'
import { useCoverageStore } from '@/stores/coverage-store'
import { useUnitBriefStore } from '@/stores/unit-brief-store'
import { useStrategyStore } from '@/stores/strategy-store'
import { useDraftingStore } from '@/stores/drafting-store'
import { useAgentStore } from '@/stores/agent-store'
import { useBriefScaffoldStore } from '@/stores/brief-scaffold-store'
import { useResponsesStore } from '@/stores/responses-store'
import { readMappedThemeIds, themesLiveOnUnitBrief } from '@/lib/brief-themes'
import type { CoverageMappingSuggestion, PlanSuggestion } from '@/lib/api'
import { toast } from 'sonner'
import { useBidReadOnly } from '@/hooks/useViewOnly'

export interface BriefModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  bidId: string
  artifactId: string
  sectionKey: string
  unitTitle: string
  scopeKind: 'unit' | 'artifact'
  canWrite: boolean
  /** Optional "Open editor" footer action — navigates into the unit's editor. */
  onOpenEditor?: () => void
}

interface SectionRequirementLink {
  linkId: string
  requirementId: string
  kind: 'compliance' | 'criterion'
}

type ListKey = 'requirements' | 'themes' | 'documents'
type NotesSaveState = 'idle' | 'saving' | 'saved' | 'error'

/** Quiet period after the last keystroke before notes save. */
export const NOTES_AUTOSAVE_DELAY_MS = 800
/** Linked rows shown per list before "Show N more". */
const COLLAPSED_ROW_LIMIT = 3

const REMOVE_BUTTON_CLASS =
  'h-6 w-6 shrink-0 text-subtle opacity-0 hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100'

// ===================
// Small presentational pieces
// ===================

function RequirementTag({
  kind,
  entry,
}: {
  kind: 'compliance' | 'criterion'
  entry: RequirementEntry | undefined
}) {
  const isMandatory = kind === 'compliance' && entry?.criticality === 'mandatory'
  let label: string
  if (kind === 'criterion') {
    const weight = entry?.weightPercentage
    label = weight != null && weight > 0 ? `Criterion · ${weight}%` : 'Criterion'
  } else {
    label = isMandatory ? 'Mandatory' : 'Compliance'
  }
  return (
    <Badge
      variant={isMandatory ? 'destructive' : 'default'}
      dot={false}
      className="mt-px w-[6.75rem] shrink-0 justify-center rounded-md px-1.5 py-0 text-[11px] font-medium"
    >
      {label}
    </Badge>
  )
}

function LoadingText({ label }: { label: string }) {
  return <span className="inline-block h-3.5 w-40 animate-pulse rounded bg-muted" aria-label={label} />
}

function LinkedRow({
  leading,
  children,
  removeLabel,
  onRemove,
}: {
  leading: ReactNode
  children: ReactNode
  removeLabel: string
  onRemove: (() => void) | null
}) {
  return (
    <li className="group flex items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-muted/40">
      {leading}
      <div className="min-w-0 flex-1 break-words text-sm leading-relaxed">{children}</div>
      {onRemove && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={removeLabel}
          onClick={onRemove}
          className={REMOVE_BUTTON_CLASS}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </li>
  )
}

function SuggestionRow({
  leading,
  title,
  reason,
  accessibleTitle,
  onAdd,
  onDismiss,
}: {
  leading: ReactNode
  title: ReactNode
  reason: string | undefined
  accessibleTitle: string
  onAdd: () => void
  onDismiss: () => void
}) {
  return (
    <li
      data-testid="brief-suggestion"
      className="flex items-start gap-2 rounded-lg border border-dashed border-[hsl(var(--brand)/0.4)] bg-[hsl(var(--brand)/0.04)] px-2 py-1.5"
    >
      {leading}
      <div className="min-w-0 flex-1">
        <p className="break-words text-sm leading-relaxed">{title}</p>
        {reason && <p className="text-meta break-words">{reason}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          variant="ai-ghost"
          size="sm"
          className="h-7 px-2"
          aria-label={`Add ${accessibleTitle}`}
          onClick={onAdd}
        >
          Add
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2"
          aria-label={`Dismiss ${accessibleTitle}`}
          onClick={onDismiss}
        >
          Dismiss
        </Button>
      </div>
    </li>
  )
}

function BriefList({
  title,
  addControl,
  rows,
  suggestions,
  emptyText,
  expanded,
  onToggleExpanded,
}: {
  title: string
  addControl: ReactNode
  rows: ReactNode[]
  suggestions: ReactNode[]
  emptyText: string
  expanded: boolean
  onToggleExpanded: () => void
}) {
  const hiddenCount = rows.length - COLLAPSED_ROW_LIMIT
  const visibleRows = expanded || hiddenCount <= 0 ? rows : rows.slice(0, COLLAPSED_ROW_LIMIT)
  return (
    <section aria-label={title} className="mt-4 border-t border-border pt-3">
      <div className="flex min-h-8 items-center justify-between gap-2">
        <h3 className="flex items-baseline gap-2 text-sm font-semibold">
          {title}
          <span className="text-meta tabular-nums">{rows.length}</span>
        </h3>
        {addControl}
      </div>
      {rows.length === 0 && suggestions.length === 0 ? (
        <p className="text-meta mt-1">{emptyText}</p>
      ) : (
        <ul className="mt-1 flex flex-col gap-1">
          {visibleRows}
          {hiddenCount > 0 && (
            <li>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-muted-foreground"
                onClick={onToggleExpanded}
              >
                {expanded ? 'Show less' : `Show ${hiddenCount} more`}
              </Button>
            </li>
          )}
          {suggestions}
        </ul>
      )}
    </section>
  )
}

/** Ghost "+ Add" list action. Forwards its ref — it sits inside Radix `asChild` triggers. */
const AddButton = forwardRef<HTMLButtonElement, { label: string } & ComponentPropsWithoutRef<typeof Button>>(
  ({ label, ...props }, ref) => (
    <Button ref={ref} type="button" variant="ghost" size="sm" className="h-7 gap-1 px-2" aria-label={label} {...props}>
      <Plus className="h-3.5 w-3.5" />
      Add
    </Button>
  ),
)
AddButton.displayName = 'AddButton'

// ===================
// Modal
// ===================

export function BriefModal({
  open,
  onOpenChange,
  bidId,
  artifactId,
  sectionKey,
  unitTitle,
  scopeKind,
  canWrite: canWriteProp,
  onOpenEditor,
}: BriefModalProps) {
  // A read-only user (an org viewer, or a member who is not on the bid) reads
  // the brief but never edits it: the backend refuses them every bid write.
  const { isReadOnly } = useBidReadOnly(bidId || undefined)
  const canWrite = canWriteProp && !isReadOnly
  // ── Section data (guidance) from the live outline ──
  const outline = useDraftingStore((s) => s.outline)
  const section = useMemo(
    () => outline?.sections?.find((s) => s.sectionKey === sectionKey) ?? null,
    [outline, sectionKey],
  )
  const guidance = section?.guidance ?? null

  // ── Document name for the eyebrow (the page already holds its artifact) ──
  const documentName = useResponsesStore((s) =>
    s.selectedArtifact?.id === artifactId
      ? s.selectedArtifact.name
      : (s.artifacts.find((a) => a.id === artifactId)?.name ?? null),
  )

  // ── Brief store (notes + pinned docs) ──
  const brief = useUnitBriefStore((s) => s.get(sectionKey))
  const saveNotesToStore = useUnitBriefStore((s) => s.saveNotes)
  const savePinsToStore = useUnitBriefStore((s) => s.savePins)
  const conflictSectionKey = useUnitBriefStore((s) => s.conflictSectionKey)
  const saveThemesToStore = useUnitBriefStore((s) => s.saveThemes)
  const notes = brief?.notes ?? null
  const pinnedDocIds = useMemo(() => brief?.pinnedDocIds ?? [], [brief?.pinnedDocIds])

  // ── Win themes: on the outline entry, or — for a single document, which has
  // no outline entry — on its unit brief (see lib/brief-themes.ts) ──
  const themesOnBrief = themesLiveOnUnitBrief(outline, sectionKey)
  const mappedThemeIds = useMemo(
    () => readMappedThemeIds(outline, sectionKey, brief),
    [outline, sectionKey, brief],
  )
  const { names: docNames, isResolving: docNamesResolving } = usePinnedDocumentNames(bidId, pinnedDocIds)

  // ── Coverage store (requirement links + suggestions) ──
  const loadCoverage = useCoverageStore((s) => s.load)
  const coverage = useCoverageStore((s) => s.coverage)
  const coverageBidId = useCoverageStore((s) => s.bidId)
  const link = useCoverageStore((s) => s.link)
  const unlink = useCoverageStore((s) => s.unlink)
  const suggestions = useCoverageStore((s) => s.suggestions)
  const isSuggesting = useCoverageStore((s) => s.isSuggesting)
  const suggest = useCoverageStore((s) => s.suggest)
  const acceptSuggestion = useCoverageStore((s) => s.acceptSuggestion)
  const rejectSuggestion = useCoverageStore((s) => s.rejectSuggestion)
  const { entries: directory, status: directoryStatus } = useRequirementDirectory(bidId)

  // ── Strategy store (confirmed win themes) ──
  const themes = useStrategyStore((s) => s.themes)
  const loadThemes = useStrategyStore((s) => s.loadThemes)
  const isLoadingThemes = useStrategyStore((s) => s.isLoadingThemes)
  const themesBidId = useStrategyStore((s) => s.currentBidId)

  // ── Drafting + agent stores ──
  const updateSectionAndSave = useDraftingStore((s) => s.updateSectionAndSave)
  const markPlanStaleForSection = useAgentStore((s) => s.markPlanStaleForSection)

  // ── Brief-open scaffold (themes + docs) ──
  const themeSuggestions = useBriefScaffoldStore((s) => s.themeSuggestions)
  const documentSuggestions = useBriefScaffoldStore((s) => s.documentSuggestions)
  const isLoadingDocScaffold = useBriefScaffoldStore((s) => s.isLoadingDocuments)
  const loadThemeScaffold = useBriefScaffoldStore((s) => s.loadThemes)
  const loadDocScaffold = useBriefScaffoldStore((s) => s.loadDocuments)
  const dismissScaffold = useBriefScaffoldStore((s) => s.dismiss)
  const clearScaffold = useBriefScaffoldStore((s) => s.clear)

  const [docPickerOpen, setDocPickerOpen] = useState(false)
  const [themePickerOpen, setThemePickerOpen] = useState(false)
  const [expandedLists, setExpandedLists] = useState<Record<ListKey, boolean>>({
    requirements: false,
    themes: false,
    documents: false,
  })
  const [suggestRan, setSuggestRan] = useState(false)
  const [isAddingAll, setIsAddingAll] = useState(false)
  // Popovers portal to the body, so Radix only keeps them inside the viewport.
  // Handing them the dialog as a collision boundary keeps them inside the modal.
  const [dialogEl, setDialogEl] = useState<HTMLDivElement | null>(null)

  const hasConflict = conflictSectionKey === sectionKey
  const isArtifactScope = scopeKind === 'artifact'
  const unitNoun = isArtifactScope ? 'document' : 'section'

  // ===================
  // Serial brief-write queue
  // ===================

  const briefWriteRef = useRef<Promise<unknown>>(Promise.resolve())
  const queueBriefWrite = useCallback(<T,>(write: () => Promise<T>): Promise<T> => {
    const next = briefWriteRef.current.catch(() => undefined).then(write)
    briefWriteRef.current = next
    return next
  }, [])

  // ===================
  // Notes autosave
  // ===================

  const [draft, setDraft] = useState(notes ?? '')
  const [notesSaveState, setNotesSaveState] = useState<NotesSaveState>('idle')
  const draftRef = useRef(draft)
  /** The trimmed notes value the server is known to hold. */
  const savedNotesRef = useRef((notes ?? '').trim())
  /** Typed since the last save was sent. */
  const dirtyRef = useRef(false)
  const inFlightRef = useRef(false)
  const pendingFlushRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** The unit the user was typing into — a flush after a unit switch still saves there. */
  const editTargetRef = useRef({ artifactId, sectionKey })
  const conflictRef = useRef(hasConflict)
  conflictRef.current = hasConflict
  const notesRef = useRef(notes)
  notesRef.current = notes

  const flushNotes = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (!dirtyRef.current) return
    // Edited elsewhere: the notice is showing and the version is stale. Never
    // overwrite silently — the draft stays on screen for the user to re-apply.
    if (conflictRef.current) return
    if (inFlightRef.current) {
      pendingFlushRef.current = true
      return
    }
    const value = draftRef.current.trim()
    dirtyRef.current = false
    if (value === savedNotesRef.current) return

    const target = editTargetRef.current
    inFlightRef.current = true
    setNotesSaveState('saving')
    void queueBriefWrite(() => saveNotesToStore(target.artifactId, target.sectionKey, value))
      .then(() => {
        savedNotesRef.current = value
        setNotesSaveState('saved')
      })
      .catch(() => {
        dirtyRef.current = true
        setNotesSaveState('error')
      })
      .finally(() => {
        inFlightRef.current = false
        if (pendingFlushRef.current) {
          pendingFlushRef.current = false
          flushRef.current()
        }
      })
  }, [queueBriefWrite, saveNotesToStore])
  const flushRef = useRef(flushNotes)
  flushRef.current = flushNotes

  const handleNotesChange = (value: string) => {
    setDraft(value)
    draftRef.current = value
    dirtyRef.current = true
    editTargetRef.current = { artifactId, sectionKey }
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => flushRef.current(), NOTES_AUTOSAVE_DELAY_MS)
  }

  // Open → start clean from the stored notes. Close → save whatever is pending.
  useEffect(() => {
    if (!open) {
      flushRef.current()
      return
    }
    const target = editTargetRef.current
    const saveInFlightHere =
      inFlightRef.current && target.artifactId === artifactId && target.sectionKey === sectionKey
    // A close-time save still in flight for this unit: the draft on screen is
    // what is being saved, and the stored notes are about to catch up.
    if (!saveInFlightHere) {
      const stored = notesRef.current ?? ''
      setDraft(stored)
      draftRef.current = stored
      savedNotesRef.current = stored.trim()
      dirtyRef.current = false
      editTargetRef.current = { artifactId, sectionKey }
    }
    setNotesSaveState('idle')
    setSuggestRan(false)
    setExpandedLists({ requirements: false, themes: false, documents: false })
  }, [open, artifactId, sectionKey])

  // Unit change or unmount (e.g. "Open editor" navigates away) → save first.
  useEffect(() => () => flushRef.current(), [artifactId, sectionKey])

  // Stored notes changed outside this editor (e.g. a reload re-hydrated the
  // brief). Adopt them only when the user has nothing unsaved — never clobber
  // text being typed, and ignore the echo of our own save.
  useEffect(() => {
    const stored = (notes ?? '').trim()
    if (stored === savedNotesRef.current) return
    if (dirtyRef.current || inFlightRef.current) return
    savedNotesRef.current = stored
    setDraft(notes ?? '')
    draftRef.current = notes ?? ''
  }, [notes])

  // ===================
  // Loads
  // ===================

  useEffect(() => {
    if (open && bidId) void loadCoverage(bidId)
  }, [open, bidId, loadCoverage])

  // Linked-theme rows render titles from the strategy store — load on open so a
  // reopened brief (or one linked from an AI suggestion) never shows a blank.
  useEffect(() => {
    if (open && bidId) void loadThemes(bidId)
  }, [open, bidId, loadThemes])

  // Scaffold: load theme suggestions on open (fast); clear on close (§3.1a).
  useEffect(() => {
    if (open && bidId && artifactId && sectionKey) {
      void loadThemeScaffold(bidId, artifactId, sectionKey)
    } else if (!open) {
      clearScaffold()
    }
  }, [open, bidId, artifactId, sectionKey, loadThemeScaffold, clearScaffold])

  // ===================
  // Derived lists
  // ===================

  const sectionLinks = useMemo<SectionRequirementLink[]>(() => {
    if (coverageBidId !== bidId || !coverage) return []
    const links: SectionRequirementLink[] = []
    const collect = (rows: typeof coverage.compliance, kind: SectionRequirementLink['kind']) => {
      for (const row of rows) {
        for (const rowLink of row.links) {
          if (rowLink.artifactId === artifactId && rowLink.sectionKey === sectionKey) {
            links.push({ linkId: rowLink.id, requirementId: row.requirementId, kind })
          }
        }
      }
    }
    collect(coverage.compliance, 'compliance')
    collect(coverage.criteria, 'criterion')
    return links
  }, [coverage, coverageBidId, bidId, artifactId, sectionKey])

  const entryById = useMemo(
    () => new Map((directory ?? []).map((entry) => [entry.id, entry])),
    [directory],
  )
  const directoryPending = directoryStatus === 'loading' || directoryStatus === 'idle'
  const linkedIds = useMemo(
    () => new Set(sectionLinks.map((l) => l.requirementId)),
    [sectionLinks],
  )
  const themeTitleById = useMemo(
    () => (themesBidId === bidId ? new Map(themes.map((t) => [t.id, t.title])) : new Map<string, string>()),
    [themes, themesBidId, bidId],
  )
  const themesUnresolved = isLoadingThemes || themesBidId !== bidId
  const linkableThemes = useMemo(
    () => themes.filter((t) => !mappedThemeIds.includes(t.id)),
    [themes, mappedThemeIds],
  )

  const requirementSuggestions = useMemo<CoverageMappingSuggestion[]>(() => {
    if (!canWrite || coverageBidId !== bidId) return []
    return suggestions.filter((s) => s.artifactId === artifactId && s.sectionKey === sectionKey)
  }, [canWrite, suggestions, coverageBidId, bidId, artifactId, sectionKey])

  const themeScaffold = useMemo(
    () =>
      canWrite
        ? themeSuggestions.filter(
            (s) =>
              s.artifactId === artifactId &&
              s.sectionKey === sectionKey &&
              !!s.themeId &&
              !mappedThemeIds.includes(s.themeId),
          )
        : [],
    [canWrite, themeSuggestions, artifactId, sectionKey, mappedThemeIds],
  )

  const docScaffold = useMemo(
    () =>
      canWrite
        ? documentSuggestions.filter(
            (s) =>
              s.artifactId === artifactId &&
              s.sectionKey === sectionKey &&
              !!s.documentId &&
              !pinnedDocIds.includes(s.documentId),
          )
        : [],
    [canWrite, documentSuggestions, artifactId, sectionKey, pinnedDocIds],
  )

  const suggestionCount = requirementSuggestions.length + themeScaffold.length + docScaffold.length
  const isFinding = isSuggesting || isLoadingDocScaffold

  // "No new suggestions" describes the run's result, not the list after the
  // user has dismissed or added everything — so the flag only survives a run
  // that came back empty.
  useEffect(() => {
    if (suggestRan && suggestionCount > 0) setSuggestRan(false)
  }, [suggestRan, suggestionCount])

  // ===================
  // Handlers
  // ===================

  const handleSuggestAll = async () => {
    setSuggestRan(false)
    const jobs: Array<Promise<void>> = [loadDocScaffold(bidId, artifactId, sectionKey)]
    // Requirement suggestions are bid-wide and slow — reuse what the store holds.
    const storeHasBidSuggestions = coverageBidId === bidId && suggestions.length > 0
    if (!storeHasBidSuggestions) jobs.push(suggest(bidId))
    await Promise.allSettled(jobs)
    setSuggestRan(true)
  }

  /**
   * Replace the unit's theme list where it lives; resolves false on failure.
   * On the brief the write joins the brief-write queue — themes share the
   * brief's version with notes and pins — and derives its list from the store
   * when it runs, so a click landing mid-write never overwrites it with a
   * stale list.
   */
  const writeThemeIds = (next: (current: string[]) => string[]): Promise<boolean> => {
    if (!themesOnBrief) {
      return updateSectionAndSave(artifactId, sectionKey, { themeIds: next(mappedThemeIds) })
    }
    return queueBriefWrite(() => {
      const current = useUnitBriefStore.getState().get(sectionKey)?.themeIds ?? []
      return saveThemesToStore(artifactId, sectionKey, next(current))
    })
  }

  const linkThemes = async (themeIds: string[]): Promise<boolean> => {
    const additions = themeIds.filter((id, i) => !mappedThemeIds.includes(id) && themeIds.indexOf(id) === i)
    if (additions.length === 0) return true
    const ok = await writeThemeIds((current) => [
      ...current,
      ...additions.filter((id) => !current.includes(id)),
    ])
    if (!ok) {
      toast.error("Couldn't link the win theme — try again.")
      return false
    }
    markPlanStaleForSection(sectionKey)
    return true
  }

  const handleLinkTheme = async (themeId: string) => {
    setThemePickerOpen(false)
    await linkThemes([themeId])
  }

  const handleAcceptThemeSuggestion = async (s: PlanSuggestion) => {
    if (!s.themeId) return
    // On failure keep the suggestion on screen — dismissing it would strip the retry.
    if (await linkThemes([s.themeId])) dismissScaffold(s.id)
  }

  const handleUnlinkTheme = async (themeId: string) => {
    const ok = await writeThemeIds((current) => current.filter((id) => id !== themeId))
    if (!ok) {
      toast.error("Couldn't unlink the win theme — try again.")
      return
    }
    markPlanStaleForSection(sectionKey)
  }

  const pinDocuments = async (suggested: PlanSuggestion[]) => {
    const ids = suggested
      .map((s) => s.documentId)
      .filter((id): id is string => !!id && !pinnedDocIds.includes(id))
    if (ids.length === 0) return
    rememberDocumentNames(
      Object.fromEntries(suggested.flatMap((s) => (s.documentId ? [[s.documentId, s.title]] : []))),
    )
    await queueBriefWrite(() => savePinsToStore(artifactId, sectionKey, [...pinnedDocIds, ...new Set(ids)]))
  }

  const handleAcceptDocSuggestion = async (s: PlanSuggestion) => {
    await pinDocuments([s])
    dismissScaffold(s.id)
  }

  const handlePinsSelected = async (ids: string[], names: Record<string, string>) => {
    rememberDocumentNames(names)
    await queueBriefWrite(() => savePinsToStore(artifactId, sectionKey, ids))
  }

  const handleUnpin = async (docId: string) => {
    await queueBriefWrite(() =>
      savePinsToStore(artifactId, sectionKey, pinnedDocIds.filter((id) => id !== docId)),
    )
  }

  const handleLinkRequirement = async (requirement: RequirementEntry) => {
    await link(
      bidId,
      requirement.kind === 'compliance'
        ? { complianceItemId: requirement.id, artifactId, sectionKey }
        : { evaluationCriterionId: requirement.id, artifactId, sectionKey },
      { artifactName: '', sectionTitle: unitTitle },
    )
    markPlanStaleForSection(sectionKey)
  }

  const handleUnlinkRequirement = async (linkId: string) => {
    await unlink(bidId, linkId)
    markPlanStaleForSection(sectionKey)
  }

  const handleAcceptRequirementSuggestion = async (s: CoverageMappingSuggestion) => {
    await acceptSuggestion(bidId, s, { artifactName: '', sectionTitle: unitTitle })
    markPlanStaleForSection(sectionKey)
  }

  const handleAddAll = async () => {
    // Snapshot: accepting drops rows from these lists as we go.
    const reqs = requirementSuggestions
    const themeRows = themeScaffold
    const docRows = docScaffold
    setIsAddingAll(true)
    try {
      // Themes and pins are each ONE write — sequential single writes would
      // each start from the same stale list and overwrite one another.
      if (themeRows.length > 0 && (await linkThemes(themeRows.flatMap((s) => (s.themeId ? [s.themeId] : []))))) {
        for (const s of themeRows) dismissScaffold(s.id)
      }
      if (docRows.length > 0) {
        await pinDocuments(docRows)
        for (const s of docRows) dismissScaffold(s.id)
      }
      for (const s of reqs) {
        await acceptSuggestion(bidId, s, { artifactName: '', sectionTitle: unitTitle })
      }
      if (reqs.length > 0) markPlanStaleForSection(sectionKey)
    } catch {
      toast.error("Couldn't add every suggestion — try again.")
    } finally {
      setIsAddingAll(false)
    }
  }

  const handleDismissAll = () => {
    for (const s of requirementSuggestions) rejectSuggestion(s)
    for (const s of [...themeScaffold, ...docScaffold]) dismissScaffold(s.id)
  }

  const toggleList = (key: ListKey) =>
    setExpandedLists((prev) => ({ ...prev, [key]: !prev[key] }))

  // ===================
  // Rows
  // ===================

  const requirementTitle = (requirementId: string): ReactNode => {
    const entry = entryById.get(requirementId)
    if (entry) return entry.title
    if (directoryPending) return <LoadingText label="Loading requirement" />
    return <span className="text-muted-foreground">Requirement unavailable</span>
  }

  const requirementRows = sectionLinks.map((l) => (
    <LinkedRow
      key={l.linkId}
      leading={<RequirementTag kind={l.kind} entry={entryById.get(l.requirementId)} />}
      removeLabel="Unlink requirement"
      onRemove={canWrite ? () => void handleUnlinkRequirement(l.linkId) : null}
    >
      {requirementTitle(l.requirementId)}
    </LinkedRow>
  ))

  const requirementSuggestionRows = requirementSuggestions.map((s) => {
    const title = entryById.get(s.requirementId)?.title ?? 'Requirement'
    return (
      <SuggestionRow
        key={`${s.requirementId}:${s.artifactId}:${s.sectionKey}`}
        leading={<RequirementTag kind={s.requirementKind} entry={entryById.get(s.requirementId)} />}
        title={requirementTitle(s.requirementId)}
        reason={s.reason}
        accessibleTitle={title}
        onAdd={() => void handleAcceptRequirementSuggestion(s)}
        onDismiss={() => rejectSuggestion(s)}
      />
    )
  })

  const themeRows = mappedThemeIds.map((themeId) => {
    const themeTitle = themeTitleById.get(themeId)
    return (
      <LinkedRow
        key={themeId}
        leading={<Flag className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />}
        removeLabel="Unlink theme"
        onRemove={canWrite ? () => void handleUnlinkTheme(themeId) : null}
      >
        {themeTitle ??
          (themesUnresolved ? (
            <LoadingText label="Loading win theme" />
          ) : (
            <span className="text-muted-foreground">Theme unavailable</span>
          ))}
      </LinkedRow>
    )
  })

  const themeSuggestionRows = themeScaffold.map((s) => (
    <SuggestionRow
      key={s.id}
      leading={<Flag className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />}
      title={s.title}
      reason={s.reason}
      accessibleTitle={s.title}
      onAdd={() => void handleAcceptThemeSuggestion(s)}
      onDismiss={() => dismissScaffold(s.id)}
    />
  ))

  const documentRows = pinnedDocIds.map((docId) => {
    const name = docNames.get(docId)
    return (
      <LinkedRow
        key={docId}
        leading={<FileText className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />}
        removeLabel="Unpin document"
        onRemove={canWrite ? () => void handleUnpin(docId) : null}
      >
        {name ??
          (docNamesResolving ? (
            <LoadingText label="Loading document name" />
          ) : (
            <span className="text-muted-foreground">Document unavailable</span>
          ))}
      </LinkedRow>
    )
  })

  const documentSuggestionRows = docScaffold.map((s) => (
    <SuggestionRow
      key={s.id}
      leading={<FileText className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />}
      title={s.title}
      reason={s.reason}
      accessibleTitle={s.title}
      onAdd={() => void handleAcceptDocSuggestion(s)}
      onDismiss={() => dismissScaffold(s.id)}
    />
  ))

  // ===================
  // Render
  // ===================

  const eyebrow = isArtifactScope
    ? 'Document brief'
    : documentName
      ? `Section brief · ${documentName}`
      : 'Section brief'

  let saveStatus: ReactNode = null
  if (hasConflict && notesSaveState !== 'idle') saveStatus = 'Not saved'
  else if (notesSaveState === 'saving') saveStatus = 'Saving…'
  else if (notesSaveState === 'error') saveStatus = "Couldn't save"
  else if (notesSaveState === 'saved' && !hasConflict)
    saveStatus = (
      <>
        <Check className="h-3.5 w-3.5" aria-hidden />
        Saved
      </>
    )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={setDialogEl} className="max-w-[720px] gap-0 p-0">
        {/* HEADER — pr-12 clears the dialog's own close button */}
        <div className="flex items-start justify-between gap-3 border-b border-border py-4 pl-5 pr-12">
          <div className="min-w-0 space-y-1">
            <p className="text-meta">{eyebrow}</p>
            <DialogTitle className="text-lg font-semibold leading-snug">{unitTitle}</DialogTitle>
          </div>
          {canWrite && (
            <Button
              type="button"
              variant="ai-outline"
              size="sm"
              className="shrink-0 gap-1.5"
              disabled={isFinding}
              onClick={() => void handleSuggestAll()}
            >
              <GradientSparkleIcon className="h-4 w-4 shrink-0" />
              {isFinding ? 'Finding…' : 'Suggest with AI'}
            </Button>
          )}
        </div>

        {/* BODY */}
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {guidance && (
            <section aria-label={`What this ${unitNoun} must do`} className="mb-5">
              <h3 className="text-sm font-semibold">What this {unitNoun} must do</h3>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{guidance}</p>
              <p className="text-meta mt-1">From the response plan</p>
            </section>
          )}

          {(canWrite || notes) && (
            <section>
              <div className="flex items-baseline gap-2">
                <h3 className="text-sm font-semibold">Your notes</h3>
                {canWrite && <span className="text-meta">optional — these win over the plan when drafting</span>}
              </div>
              {hasConflict && (
                <p className="text-meta mt-2 rounded-lg bg-muted/40 px-3 py-2">
                  This brief was edited elsewhere. Reload to see the latest, then re-apply your change.
                </p>
              )}
              {canWrite ? (
                <AutoGrowTextarea
                  value={draft}
                  onChange={(e) => handleNotesChange(e.target.value)}
                  onBlur={() => flushRef.current()}
                  placeholder="Points to hit, evidence to lean on, things to avoid."
                  aria-label="Your notes"
                  className="mt-2 min-h-[84px] rounded-lg border border-input bg-muted px-3 py-2 text-sm leading-relaxed placeholder:text-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              ) : (
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{notes}</p>
              )}
            </section>
          )}

          {canWrite && suggestionCount > 0 && (
            <div className="mt-4 flex items-center justify-between gap-3 rounded-lg bg-[hsl(var(--brand)/0.06)] px-3 py-2">
              <p className="text-sm">
                <span className="font-semibold">
                  {suggestionCount} {suggestionCount === 1 ? 'suggestion' : 'suggestions'}
                </span>{' '}
                found for this {unitNoun}
              </p>
              <div className="flex shrink-0 items-center gap-1">
                <Button type="button" variant="ghost" size="sm" onClick={handleDismissAll} disabled={isAddingAll}>
                  Dismiss all
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void handleAddAll()}
                  disabled={isAddingAll}
                >
                  Add all
                </Button>
              </div>
            </div>
          )}
          {canWrite && suggestRan && !isFinding && suggestionCount === 0 && (
            <p className="text-meta mt-4">No new suggestions for this {unitNoun}.</p>
          )}

          <BriefList
            title="Requirements to answer"
            rows={requirementRows}
            suggestions={requirementSuggestionRows}
            emptyText="No requirements linked yet."
            expanded={expandedLists.requirements}
            onToggleExpanded={() => toggleList('requirements')}
            addControl={
              canWrite && (
                <RequirementPickerPopover
                  bidId={bidId}
                  {...(dialogEl ? { collisionBoundary: dialogEl } : {})}
                  linkedIds={linkedIds}
                  onSelect={(requirement) => void handleLinkRequirement(requirement)}
                >
                  <AddButton label="Add requirement" />
                </RequirementPickerPopover>
              )
            }
          />

          <BriefList
            title="Win themes to land"
            rows={themeRows}
            suggestions={themeSuggestionRows}
            emptyText="No win themes linked yet."
            expanded={expandedLists.themes}
            onToggleExpanded={() => toggleList('themes')}
            addControl={
              canWrite && (
                <Popover open={themePickerOpen} onOpenChange={setThemePickerOpen}>
                  <PopoverTrigger asChild>
                    <AddButton label="Add win theme" onClick={() => void loadThemes(bidId)} />
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    {...(dialogEl ? { collisionBoundary: dialogEl } : {})}
                    collisionPadding={12}
                    className="w-72 p-1"
                  >
                    {linkableThemes.length === 0 ? (
                      <p className="text-meta px-2 py-3">
                        {themes.length === 0 ? 'No win themes yet — add them in Strategy.' : 'All themes already linked.'}
                      </p>
                    ) : (
                      <ul className="max-h-64 overflow-y-auto">
                        {linkableThemes.map((theme) => (
                          <li key={theme.id}>
                            <button
                              type="button"
                              onClick={() => void handleLinkTheme(theme.id)}
                              className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted/60"
                            >
                              <Flag className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                              <span className="min-w-0 break-words">{theme.title}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </PopoverContent>
                </Popover>
              )
            }
          />

          <BriefList
            title="Documents to draw on"
            rows={documentRows}
            suggestions={documentSuggestionRows}
            emptyText="No documents pinned. The AI still searches your whole library when it drafts."
            expanded={expandedLists.documents}
            onToggleExpanded={() => toggleList('documents')}
            addControl={canWrite && <AddButton label="Add documents" onClick={() => setDocPickerOpen(true)} />}
          />
        </div>

        {/* FOOTER */}
        <div className="flex min-h-12 items-center justify-between gap-3 border-t border-border px-5 py-2">
          <span className="text-meta inline-flex items-center gap-1" aria-live="polite">
            {saveStatus}
          </span>
          {onOpenEditor && (
            <Button type="button" variant="ghost" size="sm" onClick={onOpenEditor}>
              Open editor
              <ChevronRight className="ml-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            </Button>
          )}
        </div>

        {canWrite && (
          <EvidenceSelectDialog
            open={docPickerOpen}
            onOpenChange={setDocPickerOpen}
            selectedIds={pinnedDocIds}
            selectedNames={Object.fromEntries(docNames)}
            onSelectionChange={(ids, names) => void handlePinsSelected(ids, names)}
            mode="chat"
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
